import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { confirmDialog as confirm } from "@/components/ConfirmProvider";
import { playbook, takeBaseline, digestPreview, digestSend, digestSettings, nudgeStudents, studentReport } from "@/lib/campusApi";
import { openCandidateReports } from "@/lib/candidateReport";
import { Loader2, Flag, Mail, ArrowUpRight, ArrowDownRight, Send, FileText, ChevronDown, ChevronUp, Users, Target, Building2, Sparkles, Check } from "lucide-react";

/* Season playbook: the three numbers a TPO steers by, the batch in four tiers
   with what to do for each, and a baseline to re-measure against. */

const BAND = {
  top: { cls: "bg-emerald-100 text-emerald-800", label: "Top tier" },
  strong: { cls: "bg-emerald-50 text-emerald-700", label: "Strong" },
  average: { cls: "bg-amber-100 text-amber-800", label: "Average" },
  crisis: { cls: "bg-red-100 text-red-700", label: "Crisis" },
};
const TIER_CLS = {
  A: { ring: "border-emerald-300", chip: "bg-emerald-100 text-emerald-800", bar: "#22C55E" },
  B: { ring: "border-amber-300", chip: "bg-amber-100 text-amber-800", bar: "#D97706" },
  C: { ring: "border-orange-300", chip: "bg-orange-100 text-orange-800", bar: "#EA580C" },
  D: { ring: "border-stone-300", chip: "bg-stone-200 text-stone-700", bar: "#A8A29E" },
};

export default function CampusPlaybook() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const load = () => playbook().then(({ data }) => setD(data)).catch((e) => setErr(e?.response?.data?.detail || "Could not load the playbook."));
  useEffect(() => { load(); }, []);

  const baseline = async () => {
    const label = d?.baseline?.available ? "After mock drive" : "Season start";
    if (!(await confirm({ title: `Take a ${label.toLowerCase()} snapshot?`, description: "Freezes today's scores so every change from here is measured against it. You can take another after the mock placement drive.", confirmText: "Take snapshot" }))) return;
    setBusy("baseline");
    try { const { data } = await takeBaseline(label); toast.success(`Snapshot saved: ${data.readiness_pct}% ready, ${data.coverage_pct}% covered.`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not take the snapshot."); }
    finally { setBusy(""); }
  };
  const showPreview = async () => {
    setBusy("preview");
    try { const { data } = await digestPreview(); setPreview(data); }
    catch (e) { toast.error("Could not build the preview."); }
    finally { setBusy(""); }
  };
  const sendNow = async () => {
    if (!(await confirm({ title: "Email this week's digest to your team now?", description: "Owner and coordinators get the college view; HODs get their department.", confirmText: "Send" }))) return;
    setBusy("send");
    try { const { data } = await digestSend(); toast.success(`Sent to ${data.sent} team member${data.sent === 1 ? "" : "s"}.`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send."); }
    finally { setBusy(""); }
  };
  const toggleDigest = async () => {
    const enabled = !d.digest.enabled;
    try { await digestSettings(enabled); setD((x) => ({ ...x, digest: { ...x.digest, enabled } })); toast.success(enabled ? "Weekly digest on." : "Weekly digest paused."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not change this."); }
  };
  const nudgeTier = async (tier) => {
    const ids = tier.students.map((s) => s.user_id);
    if (!ids.length) return;
    if (!(await confirm({ title: `Nudge ${ids.length} ${tier.label.toLowerCase()} student${ids.length === 1 ? "" : "s"}?`, description: "Each gets a short, personal email with their one next step. Logged on their profile.", confirmText: "Send nudges" }))) return;
    setBusy("nudge" + tier.label);
    try { const { data } = await nudgeStudents(ids); toast.success(`Nudged ${data.nudged ?? ids.length} students.`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send nudges."); }
    finally { setBusy(""); }
  };
  const report = async (s) => {
    setBusy("rep" + s.user_id);
    try { const { data } = await studentReport(s.user_id); if (!openCandidateReports(data, `${data.student.name} · candidate report`)) toast.error("Allow pop-ups to open the report."); }
    catch (e) { toast.error("Could not build the report."); }
    finally { setBusy(""); }
  };

  if (err) return <div className="container-x py-14"><p className="text-red-700">{err}</p></div>;
  if (!d) return <div className="container-x py-14 text-stone-500 flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Building your playbook…</div>;
  const m = d.metrics, band = BAND[m.band.key] || BAND.average, ref = d.last_week.available ? d.last_week : d.baseline;

  return (
    <div className="container-x py-10 md:py-14" data-testid="campus-playbook">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <span className="overline">{d.college_name} · {d.scope_branch ? `${d.scope_branch} department` : "season playbook"}</span>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Know who's ready. <span className="italic text-stone-500">Move who's close.</span></h1>
          <p className="text-stone-500 mt-2 max-w-2xl">Three numbers to steer by, your batch in four tiers, and one thing to do for each tier this week. Measured against a baseline so you can prove the lift.</p>
        </div>
        {!d.scope_branch && (
          <button onClick={baseline} disabled={busy === "baseline"} data-testid="pb-baseline" className="btn-primary text-sm">
            {busy === "baseline" ? <Loader2 size={15} className="animate-spin" /> : <Flag size={15} />} {d.baseline.available ? "New checkpoint" : "Take baseline"}
          </button>
        )}
      </div>

      {/* ── The three numbers ── */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Metric label="Readiness" value={`${m.readiness_pct}%`} sub={`${m.ready} of ${m.total} students at 70+`} chip={band} note={m.band.note} delta={ref.available ? ref.readiness_delta : null} deltaLabel={ref.available ? (d.last_week.available ? "vs last week" : `vs ${ref.label || "baseline"}`) : null} testId="pb-readiness" />
        <Metric label="Coverage" value={`${m.coverage_pct}%`} sub={`${m.covered} have real evidence`} chip={m.coverage_ok ? { cls: "bg-emerald-100 text-emerald-800", label: "Above 80%" } : { cls: "bg-amber-100 text-amber-800", label: "Below 80%" }} note={m.coverage_ok ? "You can see your whole batch." : "You can't help who you can't see. Coverage first."} testId="pb-coverage" />
        {d.scope_branch ? (
          <Metric label="Batch average" value={m.avg} sub="Wishire Score, your department" chip={{ cls: "bg-stone-100 text-stone-700", label: "0 to 100" }} note="Readiness counts students at 70+; the average shows the whole department moving." testId="pb-avg" />
        ) : (
          <Metric label="Department delta" value={`${m.dept_delta} pts`} sub={m.dept_worst ? `${m.dept_best} ahead of ${m.dept_worst}` : "Needs two departments"} chip={m.dept_delta_flag ? { cls: "bg-red-100 text-red-700", label: "Wide gap" } : { cls: "bg-emerald-100 text-emerald-800", label: "Even" }} note={m.dept_delta_flag ? "A gap over 20 usually means a department is being left behind." : "Departments are moving together."} testId="pb-delta" />
        )}
      </div>

      {/* ── Baseline / movement ── */}
      {ref.available ? (
        <div className="mt-6 rounded-3xl border border-stone-200 bg-white p-6" data-testid="pb-movement">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <span className="overline">{d.last_week.available ? "Since last week" : `Since ${ref.label || "baseline"}`} · {new Date(ref.taken_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
              <p className="font-display text-2xl mt-1">
                Readiness {ref.readiness_then}% → <b>{ref.readiness_now}%</b>
                {" "}<span className={`ml-2 text-base font-sans ${ref.readiness_delta > 0 ? "text-emerald-700" : ref.readiness_delta < 0 ? "text-red-700" : "text-stone-500"}`}>({ref.readiness_delta > 0 ? "+" : ""}{ref.readiness_delta} pts)</span>
              </p>
              <p className="text-sm text-stone-500 mt-1">{ref.newly_ready} newly ready · {ref.ups_total} moved up 5+ · {ref.downs_total} slipped 5+</p>
            </div>
            {d.baseline.available && d.last_week.available && (
              <p className="text-xs text-stone-500">Since {d.baseline.label || "baseline"}: {d.baseline.readiness_then}% → {d.baseline.readiness_now}% ({d.baseline.readiness_delta > 0 ? "+" : ""}{d.baseline.readiness_delta})</p>
            )}
          </div>
          {(ref.ups.length > 0 || ref.downs.length > 0) && (
            <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
              <div><p className="text-xs font-mono uppercase tracking-widest text-emerald-700 mb-1.5">Moving up</p>{ref.ups.slice(0, 5).map((s) => <p key={s.user_id} className="flex justify-between py-0.5"><span>{s.name} <span className="text-stone-400">· {s.branch}</span></span><span className="font-mono text-emerald-700 inline-flex items-center gap-1"><ArrowUpRight size={12} />+{s.delta}</span></p>)}{!ref.ups.length && <p className="text-stone-400">No one yet.</p>}</div>
              <div><p className="text-xs font-mono uppercase tracking-widest text-red-700 mb-1.5">Slipping</p>{ref.downs.slice(0, 5).map((s) => <p key={s.user_id} className="flex justify-between py-0.5"><span>{s.name} <span className="text-stone-400">· {s.branch}</span></span><span className="font-mono text-red-700 inline-flex items-center gap-1"><ArrowDownRight size={12} />{s.delta}</span></p>)}{!ref.downs.length && <p className="text-stone-400">No one. Good.</p>}</div>
            </div>
          )}
        </div>
      ) : !d.scope_branch ? (
        <div className="mt-6 rounded-3xl border border-dashed border-stone-300 bg-stone-50 p-6 flex flex-wrap items-center justify-between gap-4" data-testid="pb-no-baseline">
          <div><p className="font-medium">No baseline yet.</p><p className="text-sm text-stone-500">Take one now, run your prep for 2 to 3 weeks, then a mock placement drive, and this card will show exactly how far the batch moved.</p></div>
          <button onClick={baseline} className="btn-primary text-sm"><Flag size={15} /> Take baseline</button>
        </div>
      ) : null}

      {/* ── Do this week ── */}
      {d.actions.length > 0 && (
        <div className="mt-6 rounded-3xl bg-stone-900 text-stone-50 p-6 wsh-grain relative overflow-hidden" data-testid="pb-actions">
          <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(420px 240px at 90% 0%, rgba(217,119,6,0.4), transparent 60%)" }} />
          <div className="relative">
            <span className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>Do this week</span>
            <ol className="mt-3 space-y-2">{d.actions.map((a, i) => <li key={i} className="flex gap-3 text-sm"><span className="font-display text-amber-300 text-lg leading-none w-5 shrink-0">{i + 1}</span><span className="text-stone-100">{a}</span></li>)}</ol>
          </div>
        </div>
      )}

      {/* ── Tiers ── */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
        {["B", "A", "C", "D"].map((k) => <TierCard key={k} k={k} tier={m.tiers[k]} total={m.total} onNudge={() => nudgeTier(m.tiers[k])} onReport={report} busy={busy} highlight={k === "B"} />)}
      </div>

      {/* ── Departments (college-wide view only) ── */}
      {!d.scope_branch && m.departments.length > 1 && (
        <div className="mt-8 rounded-3xl border border-stone-200 bg-white overflow-hidden" data-testid="pb-departments">
          <div className="px-6 py-4 border-b border-stone-100 flex items-center gap-2"><Building2 size={16} className="text-stone-400" /><span className="overline">Departments</span></div>
          <table className="w-full text-sm">
            <thead><tr className="text-xs font-mono uppercase tracking-widest text-stone-400 border-b border-stone-100">{["Department", "Students", "Readiness", "Coverage", "Avg score"].map((h) => <th key={h} className="text-left px-6 py-2.5 font-normal">{h}</th>)}</tr></thead>
            <tbody>{m.departments.map((x) => (
              <tr key={x.branch} className="border-b border-stone-50">
                <td className="px-6 py-2.5 font-medium">{x.branch}</td><td className="px-6 py-2.5 font-mono">{x.students}</td>
                <td className="px-6 py-2.5"><span className="inline-flex items-center gap-2"><span className="w-20 h-1.5 rounded-full bg-stone-100 overflow-hidden"><span className="block h-full" style={{ width: `${x.readiness_pct}%`, background: x.readiness_pct >= 50 ? "#22C55E" : x.readiness_pct >= 30 ? "#D97706" : "#DC2626" }} /></span><span className="font-mono">{x.readiness_pct}%</span></span></td>
                <td className="px-6 py-2.5 font-mono">{x.coverage_pct}%</td><td className="px-6 py-2.5 font-mono">{x.avg}</td>
              </tr>))}</tbody>
          </table>
        </div>
      )}

      {/* ── Weekly digest ── */}
      <div className="mt-8 rounded-3xl border border-stone-200 bg-white p-6" data-testid="pb-digest">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <Mail size={18} className="text-stone-400 mt-0.5" />
            <div>
              <p className="font-medium">Weekly digest to your team</p>
              <p className="text-sm text-stone-500">These numbers, movers, and this week's actions, emailed every week. Coordinators get the college; HODs get their department.{d.digest.last_sent ? ` Last sent ${new Date(d.digest.last_sent).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} to ${d.digest.recipients}.` : " Not sent yet."}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={showPreview} disabled={busy === "preview"} className="btn-ghost text-sm">{busy === "preview" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Preview mine</button>
            {d.is_owner && <button onClick={sendNow} disabled={busy === "send"} data-testid="pb-digest-send" className="btn-ghost text-sm">{busy === "send" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send now</button>}
            {d.is_owner && <button onClick={toggleDigest} className={`text-sm px-4 py-2 rounded-full border ${d.digest.enabled ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-stone-300 text-stone-600"}`}>{d.digest.enabled ? <><Check size={13} className="inline mr-1" />Weekly on</> : "Paused"}</button>}
          </div>
        </div>
        {preview && (
          <div className="mt-4 rounded-2xl bg-stone-50 border border-stone-200 p-4" data-testid="pb-digest-preview">
            <p className="text-sm font-medium">{preview.subject}</p>
            <pre className="mt-2 text-xs text-stone-700 whitespace-pre-wrap font-sans leading-relaxed">{preview.body_text}</pre>
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, sub, chip, note, delta, deltaLabel, testId }) {
  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-5" data-testid={testId}>
      <div className="flex items-center justify-between"><span className="overline">{label}</span><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${chip.cls}`}>{chip.label}</span></div>
      <p className="font-display text-5xl mt-2 leading-none">{value}{delta != null && delta !== 0 && <span className={`ml-2 text-base font-sans ${delta > 0 ? "text-emerald-700" : "text-red-700"}`}>{delta > 0 ? "+" : ""}{delta} {deltaLabel}</span>}</p>
      <p className="text-sm text-stone-600 mt-2">{sub}</p>
      <p className="text-xs text-stone-500 mt-1">{note}</p>
    </div>
  );
}

function TierCard({ k, tier, total, onNudge, onReport, busy, highlight }) {
  const [open, setOpen] = useState(highlight);
  const c = TIER_CLS[k];
  const share = total ? Math.round(100 * tier.count / total) : 0;
  return (
    <div className={`rounded-3xl border-2 ${c.ring} bg-white p-5 ${highlight ? "lg:col-span-2" : ""}`} data-testid={`pb-tier-${k}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${c.chip}`}>Tier {k} · {tier.label}</span>
          <p className="font-display text-3xl mt-2">{tier.count} <span className="text-base text-stone-400 font-sans">student{tier.count === 1 ? "" : "s"} · {share}% · score {tier.range}</span></p>
          {highlight && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-full inline-block px-2.5 py-0.5 mt-2"><Target size={11} className="inline mr-1" />Highest return: each one who crosses 70 adds {total ? (100 / total).toFixed(1) : 0}% to readiness</p>}
        </div>
        {tier.count > 0 && <button onClick={onNudge} disabled={busy === "nudge" + tier.label} className="btn-ghost text-sm"><Sparkles size={14} /> Nudge all {tier.count}</button>}
      </div>
      <p className="text-sm text-stone-700 mt-3"><b>Focus:</b> {tier.focus}</p>
      {tier.count > 0 && (
        <>
          <button onClick={() => setOpen((o) => !o)} className="mt-3 text-xs text-stone-500 inline-flex items-center gap-1">{open ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {open ? "Hide" : "Show"} students</button>
          {open && (
            <div className="mt-2 divide-y divide-stone-100 max-h-80 overflow-y-auto">
              {tier.students.map((s) => (
                <div key={s.user_id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <div className="min-w-0"><p className="font-medium truncate">{s.name} <span className="text-stone-400 font-normal">· {s.branch}</span>{s.placed && <span className="ml-1 text-[10px] font-semibold text-emerald-700">PLACED</span>}</p><p className="text-xs text-stone-500">{s.weakest ? `Weakest: ${s.weakest}` : "No evidence yet"}</p></div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-mono text-sm w-8 text-right">{s.score}</span>
                    <button onClick={() => onReport(s)} title="Recruiter report" className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-900">{busy === "rep" + s.user_id ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {tier.count === 0 && <p className="text-sm text-stone-400 mt-3 inline-flex items-center gap-1.5"><Users size={13} /> Nobody here right now.</p>}
    </div>
  );
}
